
import { IRecognizedAnswerType } from "./CompareOcrData";
import { UploadCallbacks } from "../CompareBatchNeoPenData/USB/Uploader/USBBatchStrokeUploader";
import { IPageSOBP } from "@/neolab-libs/nl-lib3-common";
import { IOcrResult } from "@/ndp/hooks/useStrokeRecognition";
import { IExamInfo } from "@/repositories/model/transfer/IExamInfo";
import ProjectSubmissionStore from "@/stores/ProjectSubmissionStore";
import ProjectStore from "@/stores/ProjectStore";
import { ProjectStateType } from "@/repositories/model/support/ProjectStateType";

export interface AnswerBatchUploaderItem {
    userCode: string;
    range: { start: IPageSOBP; end: IPageSOBP };
    codeToRecognizedText: { [questionCode: string]: IOcrResult }[];
    submissionCode?: string;
}

export default class AnswerBatchUploader {

    private projectCode: string;
    private examInfoArray: IExamInfo[];
    private projectSubmissionStore: ProjectSubmissionStore;
    private projectStore: ProjectStore;

    private items: AnswerBatchUploaderItem[];
	private callbacks: UploadCallbacks | undefined;
	private progressKey: string;
	private nextIndex: number = 0;

	private log(...args: any[]) {
		// eslint-disable-next-line no-console
		console.log("[AnswerBatchUploader]", ...args);
	}

    constructor(
            projectCode: string,
            examInfoArray: IExamInfo[],
            projectSubmissionStore: ProjectSubmissionStore,
            projectStore: ProjectStore,
            items: AnswerBatchUploaderItem[] 
        ) {

            this.projectCode = projectCode;
            this.examInfoArray = examInfoArray;
            this.projectSubmissionStore = projectSubmissionStore;
            this.projectStore = projectStore;

            this.items = items;

			this.progressKey = `AnswerBatchUploader:${projectCode}:progress`;
			this.nextIndex = this.loadProgress();
			this.log("created", { projectCode: this.projectCode, items: this.items?.length ?? 0, nextIndex: this.nextIndex });
    }

	public setCallbacks(callbacks?: UploadCallbacks) {
		this.callbacks = callbacks;
		this.log("setCallbacks");
	}

	public async upload(): Promise<{ allSucceeded: boolean; failedIndices: number[] }> {

		const projectCode = this.projectCode;
		const total = this.items?.length ?? 0;
		this.callbacks?.onStart?.(total);
		this.log("upload start", { totalItems: total, startIndex: this.nextIndex });
		for (let i = this.nextIndex; i < (this.items?.length ?? 0); i++) {
			const item = this.items[i];
			const { userCode, range, submissionCode, codeToRecognizedText } = item;
			// Merge array of maps if provided
			const strokeMap: { [questionCode: string]: IOcrResult } = Array.isArray(codeToRecognizedText)
				? Object.assign({}, ...codeToRecognizedText)
				: (codeToRecognizedText as unknown as { [questionCode: string]: IOcrResult });

			// Build answers for this item
			const answerList: IRecognizedAnswerType[] = this.createAnswersFromStrokeMap(strokeMap, this.examInfoArray);

			this.callbacks?.onItemStart?.(i, undefined as any);
			this.log("item start", { index: i, userCode, answers: Object.keys(strokeMap).length });

			let effectiveSubmissionCode = submissionCode;
			let effectiveUserCode = userCode;
			if (effectiveSubmissionCode == null) {
				try {
					const refreshedTransfer = await this.projectSubmissionStore.getSubmissionTransfersWithReturnForStudentPenSubmit(projectCode, effectiveUserCode);
					effectiveSubmissionCode = refreshedTransfer?.submissionCode;
					effectiveUserCode = refreshedTransfer?.userCode ?? effectiveUserCode;
				} catch (e) {
					// eslint-disable-next-line no-console
					console.error('[AnswerBatchUploader] Failed to refresh transfer', { projectCode, userCode: effectiveUserCode }, e);
					this.callbacks?.onItemError?.(i, undefined as any, e);
					this.nextIndex = i; this.saveProgress(this.nextIndex);
					this.callbacks?.onProgress?.(this.nextIndex, total, []);
					break;
				}
			}

			try {
				
				await this.projectSubmissionStore.uploadAnswerDataAndRenderedPdf({
						projectCode,
						userCode: effectiveUserCode,
						pdfBlobURL: null,
						answerList: answerList.map(item => ({ ...item, ocrStatus: "done" })),
						range,
						submissionCode: effectiveSubmissionCode,
				});
				await this.projectStore.updateProjectUserStateSilently(projectCode, effectiveUserCode, ProjectStateType.COMPARED, true);
				this.callbacks?.onItemSuccess?.(i, undefined as any);
				this.log("item success", { index: i, userCode: effectiveUserCode });
				// advance pointer only on success
				this.nextIndex = i + 1;
			} catch (e) {
				// eslint-disable-next-line no-console
				console.error('[AnswerBatchUploader] upload failed', { projectCode, userCode: effectiveUserCode }, e);
				this.callbacks?.onItemError?.(i, undefined as any, e);
			} finally {
				// Ensure we did not advance on error
				if (this.nextIndex !== i + 1) this.nextIndex = i;
				this.saveProgress(this.nextIndex);
				this.callbacks?.onProgress?.(this.nextIndex, total, []);
				this.log("progress", { nextIndex: this.nextIndex, total });
			}
			// stop processing further items if an error occurred for this one
			if (this.nextIndex === i) break;
		}

		if (this.nextIndex >= total) {
			this.callbacks?.onComplete?.({ failedIndices: [] });
			this.log("complete", { failed: [] });
			return { allSucceeded: true, failedIndices: [] };
		} else {
			this.callbacks?.onComplete?.({ failedIndices: [this.nextIndex] });
			this.log("stopped with failure", { nextIndex: this.nextIndex });
			return { allSucceeded: false, failedIndices: [this.nextIndex] };
		}
    }

	public async resume(): Promise<{ allSucceeded: boolean; failedIndices: number[] }> {
		this.nextIndex = this.loadProgress();
		this.log("resume", { nextIndex: this.nextIndex });
		return this.upload();
	}


	private createAnswersFromStrokeMap(strokeMap: { [questionCode: string]: IOcrResult }, examInfoArray: IExamInfo[]): IRecognizedAnswerType[] {

		// make answerslist
		const answers: IRecognizedAnswerType[] = [];

		const examList = examInfoArray;
		console.log(`ANSWER ------------------------------------------------------------------`);
		for (let examIndex = 0; examIndex < examList.length; examIndex++) {
			const questions = examList[examIndex].questions;

			for (let questionIndex = 0; questionIndex < questions.length; questionIndex++) {
				let code = examList[examIndex].code + "-" + (questionIndex + 1);
				const ocrResult = strokeMap[code];
				const { ocrStatus, recognizedText } = ocrResult || {};
				console.log(`code: ${code}, answerText: ${ocrResult}`);

				const answer: IRecognizedAnswerType = {
						projectExamCode: examList[examIndex].code,
						projectExamQuestionNum: questionIndex + 1,
						answer: recognizedText,
						ocrResult: recognizedText,
						ocrStatus
				};

				answers.push(answer);
			}
		}

		return answers;

	}

	private loadProgress(): number {
        // In-memory progress only
        return this.nextIndex || 0;
	}

	private saveProgress(index: number) {
        // In-memory progress only
        this.nextIndex = index;
	}

// removed failed tracking helpers

	public resetProgress() {
        // In-memory progress only
		this.nextIndex = 0;
	}

}